import type { ReactNode } from "react";
import { cn } from "./cn";
import {
  WarningCircle as AlertCircle,
  Warning as AlertTriangle,
  CheckCircle as CheckCircle2,
  Info,
} from "@phosphor-icons/react";

type AlertVariant = "default" | "error" | "success" | "warning" | "info";

const variantStyles: Record<AlertVariant, string> = {
  default: "bg-card border-border text-card-foreground",
  error: "bg-destructive/10 border-destructive/30 text-destructive",
  success: "bg-success/10 border-success/30 text-success",
  warning: "bg-warning/10 border-warning/30 text-warning",
  info: "bg-info/10 border-info/30 text-info",
};

const variantIcons: Record<AlertVariant, ReactNode> = {
  default: <Info className="h-4 w-4" />,
  error: <AlertCircle className="h-4 w-4" />,
  success: <CheckCircle2 className="h-4 w-4" />,
  warning: <AlertTriangle className="h-4 w-4" />,
  info: <Info className="h-4 w-4" />,
};

interface AlertProps {
  variant?: AlertVariant;
  children: ReactNode;
  className?: string;
}

/**
 * Alert component for feedback messages.
 */
export function Alert({
  variant = "default",
  children,
  className,
}: AlertProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-3 rounded-lg border p-4 text-sm",
        variantStyles[variant],
        className
      )}
    >
      <span className="mt-0.5 flex-shrink-0">{variantIcons[variant]}</span>
      <div>{children}</div>
    </div>
  );
}
